import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Search, Clock, CheckCircle2, XCircle, AlertCircle, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import LSSDLogo from "../components/LSSDLogo";

const STATUS_META = {
  pending: {
    label: "In Sospeso",
    icon: Clock,
    color: "text-amber-300",
    bg: "bg-amber-500/15",
    border: "border-amber-500/40",
    desc: "La tua candidatura è in attesa di revisione da parte dell'Ufficio Risorse Umane. Riceverai una risposta a breve.",
  },
  accepted: {
    label: "Accettata",
    icon: CheckCircle2,
    color: "text-emerald-300",
    bg: "bg-emerald-500/15",
    border: "border-emerald-500/40",
    desc: "Congratulazioni! La tua candidatura è stata accettata. Verrai contattato per i prossimi passi.",
  },
  rejected: {
    label: "Rifiutata",
    icon: XCircle,
    color: "text-rose-300",
    bg: "bg-rose-500/15",
    border: "border-rose-500/40",
    desc: "La tua candidatura non è stata accolta in questo momento. Puoi inviare una nuova domanda in futuro.",
  },
};

export default function CandidatureStatus() {
  const { code: routeCode } = useParams();
  const nav = useNavigate();
  const [code, setCode] = useState(routeCode || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!routeCode) return;
    let cancelled = false;
    (async () => {
      setError("");
      setLoading(true);
      setResult(null);
      try {
        const { data } = await api.get(`/candidatures/track/${encodeURIComponent(routeCode.trim().toUpperCase())}`);
        if (!cancelled) setResult(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404 ? "Codice non trovato. Verifica e riprova." : "Errore di connessione. Riprova.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [routeCode]);

  const lookup = async (c) => {
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/candidatures/track/${encodeURIComponent(c.trim().toUpperCase())}`);
      setResult(data);
    } catch (err) {
      setError(err.response?.status === 404 ? "Codice non trovato. Verifica e riprova." : "Errore di connessione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    nav(`/candidatura/${code.trim().toUpperCase()}`);
    lookup(code);
  };

  const meta = result ? STATUS_META[result.status] || STATUS_META.pending : null;
  const StatusIcon = meta?.icon;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="candidature-status-page">
      <div className="text-center mb-10">
        <LSSDLogo className="h-20 w-20 mx-auto mb-6" />
        <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 bg-sheriff-gold/10 border border-sheriff-gold/30 rounded-full">
          <Sparkles className="h-3.5 w-3.5 text-sheriff-gold" />
          <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-sheriff-gold">Tracciamento Candidatura</span>
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#F5E6B8] leading-tight">
          Verifica lo stato della<br /><span className="text-sheriff-gold">tua candidatura</span>
        </h1>
        <p className="text-[#D4C480] mt-4">Inserisci il codice di tracciamento che hai ricevuto dopo l&apos;invio.</p>
      </div>

      <form onSubmit={submit} className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-6 mb-6" data-testid="track-form">
        <Label className="text-xs font-semibold text-sheriff-gold mb-2 block uppercase tracking-[0.15em]">Codice di Tracciamento</Label>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="track-code-input"
            placeholder="ES. A1B2C3D4"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="font-mono text-lg tracking-[0.2em] h-12 bg-[#0A1F18] border-sheriff-gold/30 rounded-xl"
            maxLength={12}
          />
          <Button type="submit" disabled={loading || !code.trim()} data-testid="track-submit" className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold h-12 px-6">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-2" /> Verifica</>}
          </Button>
        </div>
      </form>

      {error && (
        <div data-testid="track-error" className="flex items-start gap-2 p-4 bg-rose-500/10 border border-rose-500/40 rounded-xl text-sm text-rose-300 mb-6">
          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && meta && (
        <div className={`border-2 ${meta.border} rounded-2xl overflow-hidden`} data-testid="track-result">
          <div className={`${meta.bg} p-8 text-center`}>
            <StatusIcon className={`h-16 w-16 ${meta.color} mx-auto mb-4`} />
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-sheriff-gold mb-1">Stato Candidatura</div>
            <h2 className={`font-heading font-black text-4xl ${meta.color} tracking-tight mb-2`} data-testid="track-status">{meta.label}</h2>
            <p className="text-[#E8D89A] text-sm max-w-md mx-auto leading-relaxed">{meta.desc}</p>
          </div>
          <div className="bg-[#0A1F18] p-6 sm:p-8 space-y-3">
            <Row label="Candidato" value={result.rp_name} />
            <Row label="Codice Tracciamento" value={result.tracking_code} mono />
            <Row label="Inviata il" value={new Date(result.created_at).toLocaleString("it-IT")} />
            {result.reviewed_at && <Row label="Revisionata il" value={new Date(result.reviewed_at).toLocaleString("it-IT")} />}
            {result.reviewed_by && <Row label="Revisionata da" value={`${result.reviewed_by_role ? result.reviewed_by_role + " · " : ""}${result.reviewed_by}`} />}
          </div>
          {result.hr_note && (
            <div className="bg-[#143830] border-t border-sheriff-gold/30 p-6 sm:p-8" data-testid="track-hr-note">
              <div className="flex items-center gap-2 mb-3">
                <span className="uppercase tracking-[0.18em] text-[10px] font-bold text-sheriff-gold">Messaggio dal Dipartimento</span>
              </div>
              <p className="text-[#F5E6B8] leading-relaxed whitespace-pre-wrap">{result.hr_note}</p>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 text-center">
        <Link to="/reclutamenti" className="inline-flex items-center gap-2 text-sheriff-gold hover:text-sheriff-gold-dark uppercase-label transition-colors text-sm" data-testid="track-back-link">
          <ArrowLeft className="h-4 w-4" /> Torna al modulo di candidatura
        </Link>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-sheriff-gold/10 last:border-0">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/70">{label}</div>
      <div className={`text-[#F5E6B8] text-right ${mono ? "font-mono font-bold tracking-[0.15em] text-sheriff-gold" : ""}`}>{value}</div>
    </div>
  );
}
